/**
 * Small helpers shared by the upload client(s). Split out of the old custom chunked-upload
 * implementation (resumableUpload.js) when that protocol was retired in favor of tus — these
 * three are still needed by tusUpload.js.
 */

/** Best-effort stable identity for the current user, used to namespace the tus fingerprint. */
export const getCurrentUserId = () => {
  try {
    const token = localStorage.getItem("auth_token");
    if (!token) return "anon";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.sub || payload.preferred_username || payload.email || "anon";
  } catch {
    return "anon";
  }
};

export const isOffline = () =>
  typeof navigator !== "undefined" && navigator.onLine === false;

// Unwraps our API layer's JSON-encoded error envelope back into a normal
// { response: { status, data } } shape, if present.
const unwrapApiError = (error) => {
  if (!error || error.response || error.canceled || error.code) return error;
  try {
    const details = JSON.parse(error.message);
    if (details && typeof details.status === "number") {
      const unwrapped = new Error(details.message || error.message);
      unwrapped.response = { status: details.status, data: details.data };
      return unwrapped;
    }
  } catch {
    /* error.message wasn't our JSON envelope — treat as a plain network error below */
  }
  return error;
};

export const friendlyUploadError = (error) => {
  const err = unwrapApiError(error);
  if (err?.code === "ERR_TOO_SLOW") {
    return "Upload failed because your connection is too slow or unstable. Please check your connection and try again — the upload will resume from where it stopped.";
  }
  const status = err?.response?.status;
  if (status === 413) {
    return "This file is too large to upload. Please choose a smaller file.";
  }
  if (status === 401 || status === 403) {
    return "Your session may have expired. Please sign in again and retry the upload — it will resume from where it stopped.";
  }
  if (status && status >= 500) {
    return "Upload failed due to a temporary server issue. Please try again in a moment — the upload will resume from where it stopped.";
  }
  return "Upload failed. Please try again — the upload will resume from where it stopped.";
};
